import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';
import './WorkLocationMaster.css';


// 緯度・経度は小数点以下7桁で表示する
const fmt7 = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    return Number.isNaN(n) ? v : n.toFixed(7);
};

const emptyForm = () => ({
    workplace_name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius_m: 300,
    remote_monthly_limit: 4,
    is_active: true,
});

const WorkLocationMaster = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // 修正・追加など
    const [editingId, setEditingId] = useState(null); 
    const [editData, setEditData] = useState({});  
    const [saveMsg, setSaveMsg] = useState('');

    
    const fetchLocations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/admin/workplaces/');
            setLocations(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError('データの取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLocations(); }, [fetchLocations]);

    const startEdit = (loc) => {
        setEditingId(loc.id);
        setEditData({ ...loc });
    };

    
    const editField = (field, val) => {
        setEditData(prev => ({ ...prev, [field]: val }));
    };

    // 追加
    const handleInsertEmptyRow = () => {
        if (editingId === 'NEW') {
            alert('既存の行を保存またはキャンセルしてください。');
            return;
        }
        setEditingId('NEW');
        setEditData(emptyForm()); 
    };

    
    const handleSave = async (id) => {
        setSaveMsg('');
        
        
        // 勤務地は部署非依存。必須は 勤務地名・緯度・経度 のみ。
        if (!editData.workplace_name || !editData.latitude || !editData.longitude) {
            alert('必要ところを入力してください(*)');
            return;
        }

        // 緯度・経度はサーバ側が小数点以下7桁までのため、送信前に7桁へ丸める
        // （地図からコピーすると桁数が多く、そのままでは保存に失敗するため）
        const payload = {
            ...editData,
            latitude: fmt7(editData.latitude),
            longitude: fmt7(editData.longitude),
        };

        try {
            if (id === 'NEW') {
                // 新しい行を保存する場合 (POST)
                const res = await api.post('/admin/workplaces/', payload);
                setLocations(prev => [...prev, res.data]);
                setSaveMsg('追加しました');
            } else {
                // 既存の行を更新する場合 (PATCH)
                const res = await api.patch(`/admin/workplaces/${id}/`, payload);
                setLocations(prev => prev.map(item => item.id === id ? res.data : item));
                setSaveMsg('保存しました');
            }
            setEditingId(null);
        } catch (err) {
            // サーバの検証エラーをそのまま見せる（原因が分からないと直しようがないため）
            const d = err?.response?.data;
            const detail = d && typeof d === 'object'
                ? Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`).join('\n')
                : (d || '');
            alert(`保存に失敗しました。\n${detail || '入力内容を確認してください。'}`);
        }
    };

    // 削除
    const handleDelete = async (id, name) => {
        if (!window.confirm(`「${name}」を削除しますか？`)) return;
        setSaveMsg('');
        try {
            await api.delete(`/admin/workplaces/${id}/`);
            setLocations(prev => prev.filter(item => item.id !== id));
            setSaveMsg('削除しました。');
        } catch {
            alert('削除に失敗しました。');
        }
    };

    // APIを使用して住所から緯度経度を自動取得する関数
    const handleAutoGeocodeInline = async () => {
        if (!editData.address) {
            alert("住所を入力してください。");
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/admin/workplaces/geocode/', { address: editData.address });
            if (res.data) {
                setEditData(prev => ({
                    ...prev,
                    latitude: fmt7(res.data.latitude),
                    longitude: fmt7(res.data.longitude)
                }));
                setSaveMsg(`✓ 自動的に座標を見つけました。`);
            }
        } catch (err) {
            console.error(err);
            alert("この住所は自動的に変換できません。再度確認してください。");
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (location.state?.fromWizard) {
            // 導入画面から
            navigate("/introduction");
        } else {
            // マスタ管理画面
            navigate("/admin/employee-card-setting");         }
    };

    return (
        <div className="wlm-container">
           
            <div className="wlm-header">
                <h1 className="wlm-title">勤務地マスタ</h1>
                <div className="wlm-header-actions">
                    
                    <button className="wlm-btn-add" onClick={handleInsertEmptyRow}>
                        追加 
                    </button>
                    <button className="wlm-btn-back" onClick={handleBack}>戻る</button>
                </div>
            </div>

            {saveMsg && <div className="wlm-alert-success" style={{ color: '#16a34a', marginBottom: '15px', fontWeight: 'bold' }}>{saveMsg}</div>}
            {error && <div className="wlm-alert-danger" style={{ color: '#dc2626', marginBottom: '15px' }}>{error}</div>}

            {loading && locations.length === 0 ? (
                <div className="wlm-loading">読み込み中...</div>
            ) : (
                <div className="wlm-table-wrapper">
                    <table className="wlm-table">
                        <thead>
                            <tr>
                               
                                <th>勤務地名</th>
                                <th>住所 </th>
                                <th>緯度 </th>
                                <th>経度 </th>
                                <th>半径 </th>
                                {/*<th>リモート上限 </th>
                                <th>状態 </th>
                                <th>備考 </th>*/}
                                <th style={{ minWidth: '130px' }}>操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/*現在のデータ */}
                            {locations.map(loc => {
                                const isEditing = editingId === loc.id;
                                return (
                                    <tr key={loc.id} className={isEditing ? "wlm-row-editing" : ""}>
                                        <td>
                                            {isEditing ? <input type="text" value={editData.workplace_name} onChange={e => editField('workplace_name', e.target.value)} /> : loc.workplace_name || ''}
                                        </td>
                                        <td>
                                            {isEditing ? (
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <input type="text" value={editData.address || ''} onChange={e => editField('address', e.target.value)} style={{ flex: 1 }} />
                                                    <button type="button" onClick={handleAutoGeocodeInline} style={{ padding: '2px 6px', fontSize: '0.75rem', cursor: 'pointer' }}>🔍</button>
                                                </div>
                                            ) : loc.address || ''}
                                        </td>
                                        <td>
                                            {isEditing ? <input type="number" step="any" value={editData.latitude} onChange={e => editField('latitude', e.target.value)} style={{ width: '90px' }} /> : fmt7(loc.latitude)}
                                        </td>
                                        <td>
                                            {isEditing ? <input type="number" step="any" value={editData.longitude} onChange={e => editField('longitude', e.target.value)} style={{ width: '90px' }} /> : fmt7(loc.longitude)}
                                        </td>
                                        <td>
                                            {isEditing ? <input type="number" value={editData.radius_m} onChange={e => editField('radius_m', e.target.value)} style={{ width: '60px' }} /> : `${loc.radius_m || 300}m`}
                                        </td>
                                        {/*<td>
                                            {isEditing ? <input type="number" value={editData.remote_monthly_limit} onChange={e => editField('remote_monthly_limit', e.target.value)} style={{ width: '50px' }} /> : `${loc.remote_monthly_limit || 0}日`}
                                        </td>
                                        <td>
                                            {isEditing ? (
                                                <select value={editData.is_active ? 'true' : 'false'} onChange={e => editField('is_active', e.target.value === 'true')}>
                                                    <option value="true">有効</option>
                                                    <option value="false">無効</option>
                                                </select>
                                            ) : (loc.is_active ? '有効' : '無効')}
                                        </td>
                                        <td>
                                            {isEditing ? <input type="text" value={editData.note || ''} onChange={e => editField('note', e.target.value)} /> : loc.note || ''}
                                        </td>*/}
                                        <td className="wlm-actions">
                                            {isEditing ? (
                                                <>
                                                    <button className="wlm-btn-save" onClick={() => handleSave(loc.id)}>保存</button>
                                                    <button className="wlm-btn-cancel" onClick={() => setEditingId(null)}>取消</button>
                                                </>
                                            ) : (
                                                <>
                                                    <button className="wlm-btn-edit" onClick={() => startEdit(loc)}>編集</button>
                                                    <button className="wlm-btn-delete" onClick={() => handleDelete(loc.id, loc.workplace_name)}>削除</button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}

                            {/* 追加行目 */}
                            {editingId === 'NEW' && (
                                <tr className="wlm-row-editing" style={{ backgroundColor: '#f0fdf4', border: '2px solid #22c55e' }}>
                                    
                                    <td>
                                        <input type="text" value={editData.workplace_name} onChange={e => editField('workplace_name', e.target.value)} placeholder="勤務地名" />
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            <input type="text" value={editData.address || ''} onChange={e => editField('address', e.target.value)} placeholder="住所" style={{ flex: 1 }} />
                                            <button type="button" title="軽度、緯度を自動的に表示す" onClick={handleAutoGeocodeInline} style={{ padding: '2px 6px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>🔍</button>
                                        </div>
                                    </td>
                                    <td>
                                        <input type="number" step="any" value={editData.latitude} onChange={e => editField('latitude', e.target.value)} placeholder="緯度" style={{ width: '90px' }} />
                                    </td>
                                    <td>
                                        <input type="number" step="any" value={editData.longitude} onChange={e => editField('longitude', e.target.value)} placeholder="経度" style={{ width: '90px' }} />
                                    </td>
                                    <td>
                                        <input type="number" value={editData.radius_m} onChange={e => editField('radius_m', e.target.value)} style={{ width: '60px' }} />
                                    </td>
                                    {/*<td>
                                        <input type="number" value={editData.remote_monthly_limit} onChange={e => editField('remote_monthly_limit', e.target.value)} style={{ width: '50px' }} />
                                    </td>
                                    <td>
                                        <select value={editData.is_active ? 'true' : 'false'} onChange={e => editField('is_active', e.target.value === 'true')}>
                                            <option value="true">有効</option>
                                            <option value="false">無効</option>
                                        </select>
                                    </td>
                                    <td>
                                        <input type="text" value={editData.note || ''} onChange={e => editField('note', e.target.value)} placeholder="備考" />
                                    </td>*/}
                                    <td className="wlm-actions">
                                        <button className="wlm-btn-save" style={{ background: '#16a34a' }} onClick={() => handleSave('NEW')}>保存</button>
                                        <button className="wlm-btn-cancel" onClick={() => setEditingId(null)}>取消</button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default WorkLocationMaster;